import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ActiveTable } from '@/components/reviews-client-page/reviews-client-page.types';
import {
    AUTO_ROWS_ON_PAGE_FIT_ENABLED,
    buildRowsPerPagePresets,
    calculateRowsFitOnScreen,
} from '@/components/reviews-client-page/reviews-client-page-rows-fit.helpers';

type UseReviewsClientPageRowsFitArgs = {
    activeTable: ActiveTable;
    reviewsOnPage: number;
    setReviewsOnPage: (count: number) => Promise<void>;
    productsOnPage: number;
    setProductsOnPage: (count: number) => void;
    setProductsPage: (page: number) => void;
    brandsOnPage: number;
    setBrandsOnPage: (count: number) => void;
    setBrandsPage: (page: number) => void;
};

const reviewsPageRowsFitLogger = createClientLogger({
    scope: 'reviews-page-rows-fit',
});

export const useReviewsClientPageRowsFit = ({
    activeTable,
    reviewsOnPage,
    setReviewsOnPage,
    productsOnPage,
    setProductsOnPage,
    setProductsPage,
    brandsOnPage,
    setBrandsOnPage,
    setBrandsPage,
}: UseReviewsClientPageRowsFitArgs) => {
    const [fittedRowsOnScreen, setFittedRowsOnScreen] = useState<number | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const topControlsContainerRef = useRef<HTMLDivElement | null>(null);
    const hasAppliedFittedRowsRef = useRef(false);

    const calculateRowsFitOnScreenFromRefs = useCallback(() => {
        return calculateRowsFitOnScreen({
            scrollContainerHeight: scrollContainerRef.current?.clientHeight ?? 0,
            topControlsHeight: topControlsContainerRef.current?.offsetHeight ?? 0,
        });
    }, []);

    const rowsPerPagePresets = useMemo(
        () =>
            buildRowsPerPagePresets({
                autoRowsOnPageFitEnabled: AUTO_ROWS_ON_PAGE_FIT_ENABLED,
                fittedRowsOnScreen,
            }),
        [fittedRowsOnScreen],
    );

    useEffect(() => {
        if (!AUTO_ROWS_ON_PAGE_FIT_ENABLED) {
            return;
        }

        if (typeof window === 'undefined') {
            return;
        }

        const updateFittedRows = () => {
            const rowsOnScreen = calculateRowsFitOnScreenFromRefs();
            if (!rowsOnScreen) {
                return;
            }

            setFittedRowsOnScreen((currentRowsOnScreen) =>
                currentRowsOnScreen === rowsOnScreen ? currentRowsOnScreen : rowsOnScreen,
            );
        };

        updateFittedRows();
        const animationFrameId = window.requestAnimationFrame(updateFittedRows);

        const onResize = () => {
            updateFittedRows();
        };

        const viewport = window.visualViewport;
        viewport?.addEventListener('resize', onResize);
        window.addEventListener('resize', onResize);

        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateFittedRows);
        if (resizeObserver && scrollContainerRef.current) {
            resizeObserver.observe(scrollContainerRef.current);
        }
        if (resizeObserver && topControlsContainerRef.current) {
            resizeObserver.observe(topControlsContainerRef.current);
        }

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            viewport?.removeEventListener('resize', onResize);
            window.removeEventListener('resize', onResize);
            resizeObserver?.disconnect();
        };
    }, [activeTable, calculateRowsFitOnScreenFromRefs]);

    useEffect(() => {
        if (!AUTO_ROWS_ON_PAGE_FIT_ENABLED || !fittedRowsOnScreen || hasAppliedFittedRowsRef.current) {
            return;
        }

        hasAppliedFittedRowsRef.current = true;

        if (productsOnPage !== fittedRowsOnScreen) {
            setProductsOnPage(fittedRowsOnScreen);
            setProductsPage(1);
        }

        if (brandsOnPage !== fittedRowsOnScreen) {
            setBrandsOnPage(fittedRowsOnScreen);
            setBrandsPage(1);
        }

        if (reviewsOnPage !== fittedRowsOnScreen) {
            void setReviewsOnPage(fittedRowsOnScreen).catch((error) => {
                logClientError({
                    logger: reviewsPageRowsFitLogger,
                    message:
                        '[client.reviews-page.rows-per-page-persist] Failed to persist auto-fitted reviews rows per page.',
                    error,
                    context: {
                        fittedRowsOnScreen,
                    },
                });
            });
        }
    }, [
        brandsOnPage,
        fittedRowsOnScreen,
        productsOnPage,
        reviewsOnPage,
        setBrandsOnPage,
        setBrandsPage,
        setProductsOnPage,
        setProductsPage,
        setReviewsOnPage,
    ]);

    return {
        rowsPerPagePresets,
        scrollContainerRef,
        topControlsContainerRef,
    };
};
